import React, { useEffect, useRef } from "react";
import "./PrimaryFeature.css";

export default function PrimaryFeature() {
  const containerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    // Original observer for individual card animations (fade-in)
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -15% 0px",
      }
    );

    // New observer for reveal complete (switches to normal flow after last card ~50% visible)
    const revealObserver = new IntersectionObserver(
      (entries) => {
        const lastEntry = entries[0];
        if (lastEntry.isIntersecting && lastEntry.intersectionRatio >= 0.5) {
          containerRef.current?.classList.add("section-revealed");
        }
      },
      {
        threshold: 0.5,
        rootMargin: "0px 0px -20% 0px", // Slight bottom margin to trigger a bit earlier
      }
    );

    const cards = containerRef.current?.querySelectorAll(
      ".feature-card-wrapper"
    );
    if (cards) {
      cardRefs.current = Array.from(cards);
      cardRefs.current.forEach((card) => observer.observe(card));

      // Observe only the last card for reveal switch
      if (cardRefs.current.length > 0) {
        revealObserver.observe(cardRefs.current[cardRefs.current.length - 1]);
      }
    }

    return () => {
      cardRefs.current.forEach((card) => {
        if (card) {
          observer.unobserve(card);
          revealObserver.unobserve(card); // Safe, even if not observed
        }
      });
      cardRefs.current = [];
    };
  }, []);

  return (
    <section
      id="primary-feature"
      className="diff-section-container stacked-mode primary-header-alignment"
      ref={containerRef}
    >
      {/* Sticky Header */}
      <div className="diff-header">
        <span className="feature-pill">Features</span>
        <h2 className="header-title">A Complete Marketing Ecosystem</h2>
        <p className="header-subtitle">
          Adflyn combines AI-powered chatbots, automated poster creation, and
          intelligent social media scheduling in one platform. Generate leads,
          create content, and manage campaigns-all without manual effort.
        </p>
      </div>
      {/* Card 1 - AI Analytics */}
      <div className="feature-card-wrapper card-1">
        <div className="left-content">
          <h3>Adflyn Chat Assistant</h3>
          <p>
            Your always-on AI marketing assistant that helps you plan, create,
            and execute marketing tasks—instantly and effortlessly. <br></br>
            <br></br> Ask questions, generate content, and get actionable
            marketing guidance in real time, all from a single chat interface.
          </p>
          <a href="https://app.youform.com/forms/qw5x4urb" target="_blank" rel="noopener noreferrer" className="waiting-list-btn">
            Join our waitlist!{" "}
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
        <div className="right-visual">
          <div className="trending-card">
            <div className="card-pill">What you get</div>
            <ul className="feature-list">
              <li className="feature-list">
                Instant marketing answers in under 5 seconds
              </li>
              <li className="feature-list">
                Content ideas, captions, and copy on demand
              </li>
              <li className="feature-list">
                Poster creation through simple chat prompts
              </li>
              <li className="feature-list">
                Campaign and strategy recommendations anytime
              </li>
              <li className="feature-list">One AI assistant available 24/7</li>
              <li className="feature-list">
                Faster decision-making with less manual research
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* Card 2 - Publishing */}
      <div className="feature-card-wrapper card-2">
        <div className="left-content">
          <h3>AI Poster Creation</h3>
          <p>
            Create professional, high-quality social media posters in seconds
            designed to look polished, on-brand, and ready to publish across
            platforms. <br></br>
            <br></br>Adflyn’s AI applies proven design principles, layout
            balance, and brand consistency automatically, so every poster looks
            like it was made by a professional designer.
          </p>
          <a href="https://app.youform.com/forms/qw5x4urb" target="_blank" rel="noopener noreferrer" className="waiting-list-btn">
            Join our waitlist!
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
        <div className="right-visual">
          <div className="trending-card">
            <div className="card-pill">What you get</div>
            <ul className="feature-list">
              <li className="feature-list">
                Studio-quality posters generated in under 20 seconds
              </li>
              <li className="feature-list">
                Clean, professional layouts optimized for engagement
              </li>
              <li className="feature-list">
                Brand-aligned colors, fonts, and visual styles
              </li>
              <li className="feature-list">
                10–15 design variations from a single input
              </li>
              <li className="feature-list">
                Platform-ready sizes for Instagram, Facebook, WhatsApp & ads
              </li>
              <li className="feature-list">
                Consistent, premium-looking creatives for 100% of your posts
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* Card 3 - Automation */}
      <div className="feature-card-wrapper card-3">
        <div className="left-content">
          <h3>Social Media Post Automation</h3>
          <br></br>
          <p>
            Schedule, create, and publish content across all major platforms
            automatically. <br></br> <br></br> Adflyn uses AI to plan posts,
            optimise timing, and adapt content for each channel—so your brand
            stays active without daily manual work.
          </p>
          <a href="https://app.youform.com/forms/qw5x4urb" target="_blank" rel="noopener noreferrer" className="waiting-list-btn">
            Join our waitlist!
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
        <div className="right-visual">
          <div className="trending-card">
            <div className="card-pill">What you get</div>
            <ul className="feature-list">
              <li className="feature-list">
                AI-generated posts in under 30 seconds
              </li>
              <li className="feature-list">
                Automated scheduling that saves 8–12 hours per week
              </li>
              <li className="feature-list">
                Platform-optimized posts for 3–5 social channels
              </li>
              <li className="feature-list">
                30–90 posts/month published on autopilot
              </li>
              <li className="feature-list">
                Consistent posting with 0 missed days
              </li>
              <li className="feature-list">
                One dashboard to manage 100% of your social content
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
